<script setup lang="ts">
  import type {ReviewStats} from '~';
  import {computed} from '#imports';

  const props = withDefaults(
    defineProps<{
      stats: ReviewStats;
      layout?: 'stacked' | 'inline';
      handle?: string;
    }>(),
    {
      layout: 'inline',
      handle: undefined,
    },
  );

  const getPercentage = (rating: number) => {
    if (!props.stats) {
      return 0;
    }

    if (!props.stats.totalReviewsByRating[rating]) {
      return 0;
    }

    return (
      (props.stats.totalReviewsByRating[rating] / props.stats.totalReviews) *
      100
    );
  };

  const ratingsText = computed(() => {
    return props.stats.totalReviews === 1 ? 'rating' : 'ratings';
  });
</script>

<template>
  <div>
    <template v-if="stats">
      <div
        :class="{
          grid: true,
          'gap-6 xl:grid-cols-2 xl:gap-20 items-end ': layout === 'inline',
          'flex-col': layout === 'stacked',
        }"
      >
        <div class="flex flex-wrap items-baseline gap-4">
          <div class="flex items-baseline gap-1">
            <span class="text-[3.25rem] leading-none font-light">
              {{ stats.ratingAvg }}
            </span>
            <span class="text-2xl font-light text-gray-500">/ 5</span>
          </div>
          <RatingStars
            :rating="stats.ratingAvg"
            size="xl"
            class="relative top-1"
          />

          <div class="xl:ml-auto text-gray-500">
            {{ stats.totalReviews }} {{ ratingsText }}
          </div>
        </div>

        <div class="flex-grow">
          <div class="space-y-1">
            <template v-for="i in [5, 4, 3, 2, 1]" :key="i">
              <div class="rating-bar">
                <span
                  class="text-light text-xs font-mono inline-flex text-right"
                  >{{ i }}</span
                >
                <ProgressBar
                  :value="getPercentage(i)"
                  :height="10"
                  indicator-class="bg-yellow-500 dark:bg-yellow-400"
                />
                <span class="text-light text-xs font-mono text-left"
                  >{{ Number(getPercentage(i)).toFixed(0) }}%</span
                >
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="postcss">
  .rating-bar {
    display: grid;
    grid-template-columns: auto 1fr 4ch;
    align-items: center;
    gap: theme('spacing.2');
  }
</style>
